import React from "react";
import Navbar from "./components/Navbar";
import Footer from "./components/Footer";
import { Link } from "react-router-dom";

const tocItems = [
  { id: "eligibility", label: "1. Refund Eligibility" },
  { id: "reporting", label: "2. How to Report Issues" },
  { id: "process", label: "3. Refund Processing" },
  { id: "nonrefundable", label: "4. Non-Refundable Cases" },
  { id: "exchange", label: "5. Exchange Policy" },
  { id: "cancellations", label: "6. Order Cancellations" },
  { id: "contact", label: "7. Customer Support" },
];

export default function RefundPolicyPage() {
  return (
    <>
      <Navbar />
      <div className="policy-page">
        <div className="policy-container">
          <div className="breadcrumb__list mb-30">
            <span>
              <Link to="/">Home</Link>
            </span>
            <span className="dvdr">
              <i className="fa-regular fa-angle-right"></i>
            </span>
            <span>Refund Policy</span>
          </div>
          <header className="policy-header">
            <h1>Refund Policy</h1>
            <p className="policy-updated">
              <strong>Last updated:</strong> January 01, 2026
            </p>
            <p>
              At <strong>A1 Bakery and Sweets</strong>, we strive to ensure your
              complete satisfaction with every order. This Refund Policy
              outlines when and how you can request refunds, replacements, or
              exchanges for your purchases.
            </p>
            <p>
              Please read this policy carefully to understand your rights and
              our refund procedures.
            </p>
          </header>

          <section className="policy-toc">
            <h2>Quick Navigation</h2>
            <ul>
              {tocItems.map((item) => (
                <li key={item.id}>
                  <a href={`#${item.id}`}>{item.label}</a>
                </li>
              ))}
            </ul>
          </section>

          <section id="eligibility" className="policy-section">
            <h2>1. Refund Eligibility</h2>
            <p>
              We process refunds for orders that meet specific quality and
              delivery criteria:
            </p>

            <h3>Quality-Related Refunds</h3>
            <ul>
              <li>
                <strong>Damaged Products:</strong> Broken packaging, crushed
                items, or physically damaged sweets/bakery items
              </li>
              <li>
                <strong>Spoiled Items:</strong> Products received in poor
                condition due to transportation issues
              </li>
              <li>
                <strong>Quality Issues:</strong> Items not meeting our freshness
                standards (stale, off-taste, improper texture)
              </li>
            </ul>

            <h3>Order Accuracy Issues</h3>
            <ul>
              <li>
                <strong>Incorrect Items:</strong> Wrong products delivered
                compared to your order confirmation
              </li>
              <li>
                <strong>Missing Items:</strong> Items listed in your order but
                not delivered
              </li>
              <li>
                <strong>Quantity Errors:</strong> Fewer items received than
                ordered
              </li>
            </ul>

            <p>
              <strong>Note:</strong> All quality issues must be reported within{" "}
              <strong>24 hours</strong> of delivery.
            </p>
          </section>

          <section id="reporting" className="policy-section">
            <h2>2. How to Report Issues</h2>
            <p>
              To request a refund or replacement, please contact us within{" "}
              <strong>24 hours</strong> of delivery with:
            </p>

            <ul>
              <li>
                Your <strong>Order Number</strong>
              </li>
              <li>
                <strong>Clear photos</strong> showing the issue (damage, wrong
                item, etc.)
              </li>
              <li>
                <strong>Detailed description</strong> of the problem
              </li>
              <li>Time of delivery (if possible)</li>
            </ul>

            <p>
              Email all details to{" "}
              <a href="mailto:support@a1bakeryandsweets.com">
                support@a1bakeryandsweets.com
              </a>
              or WhatsApp us at <strong>9751336638</strong>.
            </p>
          </section>

          <section id="process" className="policy-section">
            <h2>3. Refund Processing Timeline</h2>

            <div className="timeline">
              <div className="timeline-step">
                <div className="step-number">1</div>
                <div className="step-content">
                  <h4>Review (3-5 Business Days)</h4>
                  <p>We verify your claim and photos</p>
                </div>
              </div>
              <div className="timeline-step">
                <div className="step-number">2</div>
                <div className="step-content">
                  <h4>Approval Notification</h4>
                  <p>You receive confirmation via email/WhatsApp</p>
                </div>
              </div>
              <div className="timeline-step">
                <div className="step-number">3</div>
                <div className="step-content">
                  <h4>Refund Issued (5-10 Business Days)</h4>
                  <p>Funds return to original payment method</p>
                </div>
              </div>
            </div>

            <p>
              <strong>Processing Note:</strong> Refund timelines depend on your
              bank/payment provider. UPI refunds typically appear faster.
            </p>
          </section>

          <section id="nonrefundable" className="policy-section">
            <h2>4. Non-Refundable Situations</h2>
            <p>We cannot process refunds in the following cases:</p>

            <ul>
              <li>
                <strong>Change of Mind:</strong> Simply deciding you no longer
                want the product
              </li>
              <li>
                <strong>Customer Address Error:</strong> Wrong delivery address
                provided by customer
              </li>
              <li>
                <strong>Uncollected Orders:</strong> Orders not picked up from
                delivery partner
              </li>
              <li>
                <strong>Late Reporting:</strong> Issues reported after 24-hour
                window
              </li>
              <li>
                <strong>Partial Consumption:</strong> Opened/eaten products
                without clear quality issues
              </li>
            </ul>
          </section>

          <section id="exchange" className="policy-section">
            <h2>5. Exchange Policy</h2>
            <p>
              When refunds aren't possible but replacement is feasible, we offer
              exchanges subject to:
            </p>

            <ul>
              <li>Product availability at time of exchange</li>
              <li>Same-day delivery feasibility in your area</li>
              <li>Customer covers return shipping (unless our error)</li>
            </ul>

            <p>
              <strong>Exchange Priority:</strong> We prefer replacements for
              quality/accuracy issues when available.
            </p>
          </section>

          <section id="cancellations" className="policy-section">
            <h2>6. Order Cancellations</h2>
            <ul>
              <li>
                <strong>Time Window:</strong> Within <strong>1 hour</strong> of
                placing order
              </li>
              <li>
                <strong>Status:</strong> Only if order not yet prepared/packed
              </li>
              <li>
                <strong>Method:</strong> WhatsApp <strong>9751336638</strong> or
                email{" "}
                <a href="mailto:support@a1bakeryandsweets.com">
                  support@a1bakeryandsweets.com
                </a>
              </li>
            </ul>

            <p>
              <strong>Important:</strong> After 1 hour or once dispatched,
              cancellations aren't guaranteed. Standard refund policy applies.
            </p>
          </section>

          <section id="contact" className="policy-section">
            <h2>7. Customer Support</h2>

            <div className="contact-grid">
              <div className="contact-card">
                <h3>Immediate Help</h3>
                <p>
                  <strong>WhatsApp:</strong>{" "}
                  <a href="https://wa.me/919751336638">9751336638</a>
                </p>
                <p>
                  <strong>Call:</strong> 9751336638
                </p>
              </div>

              <div className="contact-card">
                <h3>Email Support</h3>
                <p>
                  <a href="mailto:support@a1bakeryandsweets.com">
                    support@a1bakeryandsweets.com
                  </a>
                </p>
              </div>

              <div className="contact-card full-width">
                <h3>Return Address</h3>
                <p>
                  <strong>A1 Bakery and Sweets</strong>
                  <br />
                  123/4, Anna Salai Street,
                  <br />
                  Near Periyar Bus Stand,
                  <br />
                  Madurai, Tamil Nadu 625001
                </p>
              </div>
            </div>
          </section>
        </div>
      </div>
      <Footer />
    </>
  );
}
